/**
 * @file ContentTab.js
 * @description Component for rendering the content tab of the message page.
 **/
import { useState, useEffect, useMemo, useRef } from 'react';
import { CharacterLimitIndicator } from '../helpers/CharacterLimitIndicator';
import RichTextEditor from '../new-portal-page/RichTextEditor';
import { useWindowListener } from '../helpers/useWindowListener';
import { formatDateToMmmDDYYYY } from '../helpers/DateUtils';
import { FileDrop } from '../helpers/FileDrop';
import Toast from '../ui/toasts/Toast';
import { uploadFile } from '../../services/containerService';
import { useAuthContext, useUploadContext } from '../../store/Contexts';
import {
    formatFileSize,
    getAudioLength,
    getFileCategory,
    getImageDimensions,
    getVideoRuntime,
    sanitizeFileName,
} from '../helpers/FileUtils';
import { createAsset, fetchAssets } from '../../services/clientService';
import { queryPortalPages } from '../../services/portalPageService';
import { getPushDestinationTypes } from '../../services/messageService';
import { InputField, SwitchField } from '../helpers/FormComponents';
import Tooltip from '../ui/tooltips/Tooltip';
import TagInput from '../helpers/TagInput';
import { Dropdown } from '../helpers/Dropdown';
import SSODropdown from '../shared/asyncDropdowns/SSODropdown';
import LoadingWrapper from '../ui/loaders/LoadingWrapper';
import {
    PUSH_MENU_DESTINATIONS,
    PUSH_DESTINATION_OPTIONS,
} from '../../utils/data/enums';
import styles from '../assets/AssetLibrary.module.scss';
import AssetGridView from '../assets/components/AssetGridView';
import PaginationComponent from '../helpers/PaginationComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import { isValidFileTypeAndSize } from '../../utils/validate';
import { getText } from '../../i18n';
import {
    htmlToPlainText,
    sanitizeNotificationChannelText,
    shouldAutoSyncChannel,
} from '../../utils/htmlText';
import SideDrawer from '../helpers/SideDrawer';
import contentStyles from './ContentTab.module.scss';

const CONTENT_CHANNELS = {
    EMAIL: 'email',
    SMS: 'sms',
    PUSH: 'push',
};

const CHANNEL_TABS = [
    {
        value: CONTENT_CHANNELS.EMAIL,
        label: getText('msg_84add5b29527'),
        icon: 'bi-envelope',
    },
    {
        value: CONTENT_CHANNELS.SMS,
        label: getText('msg_9e10d4517d5a'),
        icon: 'bi-chat-text',
    },
    {
        value: CONTENT_CHANNELS.PUSH,
        label: getText('msg_8f7f57b519fc'),
        icon: 'bi-bell',
    },
];

const PUSH_DESTINATIONS_WITHOUT_LINK = {
    'full-message': true,
};

const MENU_LINK_OPTIONS = PUSH_MENU_DESTINATIONS.map(destination => ({
    value: destination.value,
    label: getText(destination.labelKey),
}));

const MESSAGE_DEFINITIONS_SECTIONS = [
    {
        label: getText('msg_971d6ded4edf'),
        text: getText('msg_88c218cfb2d1'),
    },
    {
        label: getText('msg_cf73be80fa6a'),
        text: getText('msg_f215107d6a76'),
    },
    {
        label: getText('msg_0b475609baf3'),
        text: getText('msg_0011a3b60a68'),
    },
    {
        label: getText('msg_d42713493ca8'),
        text: getText('msg_36d9a16d546a'),
    },
];

/**
 * Renders a form for displaying and editing message content.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.data - The initial data for the form.
 * @param {Function} props.onChange - The callback function to be called when the form data changes.
 * @returns {JSX.Element} The rendered ContentTab component.
 */
const ContentTab = ({
    data,
    contentSeedKey,
    subjectSeed = '',
    emailEditorSeed = '',
    smsEditorSeed = '',
    pushEditorSeed = '',
    onChange,
    useWideLayout = false,
}) => {
    const [formData, setFormData] = useState({
        ...data,
        subject: subjectSeed,
        pushDestination: data.pushDestination || 'full-message',
        pushLink: data.pushLink ?? '',
    });
    const [emailInitialContent, setEmailInitialContent] = useState(
        emailEditorSeed || '',
    );
    const [smsInitialContent, setSmsInitialContent] = useState(
        smsEditorSeed || '',
    );
    const [pushInitialContent, setPushInitialContent] = useState(
        pushEditorSeed || '',
    );

    const [plainText, setPlainText] = useState(smsEditorSeed || '');
    const [isSmsAutoLinked, setIsSmsAutoLinked] = useState(
        !htmlToPlainText(smsEditorSeed || '').trim(),
    );
    const [isPlainTextCustom, setIsPlainTextCustom] = useState(
        !!htmlToPlainText(smsEditorSeed || '').trim(),
    );
    const [pushText, setPushText] = useState(pushEditorSeed || '');
    const [isPushAutoLinked, setIsPushAutoLinked] = useState(
        !htmlToPlainText(pushEditorSeed || '').trim(),
    );
    const [isPushTextCustom, setIsPushTextCustom] = useState(
        !!htmlToPlainText(pushEditorSeed || '').trim(),
    );
    const [file, setFile] = useState(null);
    const { user } = useAuthContext();
    const { enqueueUpload } = useUploadContext();
    const [isAddFromAssetsChecked, setIsAddFromAssetsChecked] = useState('');
    const [isUploadFromDeviceChecked, setIsUploadFromDeviceChecked] =
        useState('');
    const [imageAsset1, setImageAsset1] = useState({});
    const [imageAsset2, setImageAsset2] = useState({});
    const [imageAsset3, setImageAsset3] = useState({});
    const [imageAsset4, setImageAsset4] = useState({});
    const [videoAsset, setVideoAsset] = useState({});
    const [textAssets, setTextAssets] = useState([]);
    const [text, setText] = useState('');
    const [videoUrl, setVideoUrl] = useState('');
    const [asset, setAsset] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [assets, setAssets] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeCard, setActiveCard] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [activeChannel, setActiveChannel] = useState(CONTENT_CHANNELS.EMAIL);
    const [isDefinitionsDrawerOpen, setIsDefinitionsDrawerOpen] =
        useState(false);
    const [portalOptions, setPortalOptions] = useState([]);
    const [hasLoadedPortalOptions, setHasLoadedPortalOptions] = useState(false);
    const [assetOptions, setAssetOptions] = useState([]);
    const [hasLoadedAssetOptions, setHasLoadedAssetOptions] = useState(false);
    const [pushDestinationTypes, setPushDestinationTypes] = useState([]);
    const [isLoadingPushDestinationTypes, setIsLoadingPushDestinationTypes] =
        useState(true);
    const [smsEditorVersion, setSmsEditorVersion] = useState(0);
    const [pushEditorVersion, setPushEditorVersion] = useState(0);
    const lastHydratedSeedRef = useRef(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const historyRef = useRef(null);

    const pushDestinationOptions = useMemo(() => {
        if (isLoadingPushDestinationTypes) {
            return [];
        }

        const availableOptions =
            pushDestinationTypes.length > 0
                ? PUSH_DESTINATION_OPTIONS.filter(option =>
                      pushDestinationTypes.some(
                          destinationType =>
                              destinationType.id === option.typeId,
                      ),
                  )
                : PUSH_DESTINATION_OPTIONS;

        return availableOptions.map(option => ({
            value: option.value,
            label: getText(option.labelKey),
        }));
    }, [pushDestinationTypes, isLoadingPushDestinationTypes]);

    const overlayBackground = {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        zIndex: 999,
        overflow: 'auto',
        boxSizing: 'border-box',
        backgroundColor: 'rgba(255, 255, 255, 0.56)',
        display: 'flex',
        padding: '20px',
        justifyContent: 'center',
    };

    const overlayContainer = {
        width: '620px',
        height: '740px',
        maxHeight: '100hv',
        overflow: 'auto',
        boxSizing: 'border-box',
        backgroundColor: 'white',
        padding: '20px',
    };

    useEffect(() => {
        if (!contentSeedKey) return;
        if (lastHydratedSeedRef.current === contentSeedKey) return;

        lastHydratedSeedRef.current = contentSeedKey;

        setFormData({
            ...data,
            subject: subjectSeed,
            pushDestination: data.pushDestination || 'full-message',
            pushLink: data.pushLink ?? '',
        });

        // seed the editors once for this loaded notification/template
        setEmailInitialContent(emailEditorSeed || '');
        setSmsInitialContent(smsEditorSeed || '');
        setPushInitialContent(pushEditorSeed || '');

        // current editable values
        setPlainText(smsEditorSeed || '');
        setIsPlainTextCustom(!!htmlToPlainText(smsEditorSeed || '').trim());
        setIsSmsAutoLinked(!htmlToPlainText(smsEditorSeed || '').trim());

        setPushText(pushEditorSeed || '');
        setIsPushTextCustom(!!htmlToPlainText(pushEditorSeed || '').trim());
        setIsPushAutoLinked(!htmlToPlainText(pushEditorSeed || '').trim());
        setSmsEditorVersion(prev => prev + 1);
        setPushEditorVersion(prev => prev + 1);
    }, [
        contentSeedKey,
        data,
        subjectSeed,
        emailEditorSeed,
        smsEditorSeed,
        pushEditorSeed,
    ]);

    useWindowListener('mousedown', event => {
        if (historyRef.current && !historyRef.current.contains(event.target)) {
            setIsHistoryOpen(false);
        }
    });

    const handleOnChangeTitle = event => {
        const nextSubject = event.target.value;

        setFormData(prevDetails => ({
            ...prevDetails,
            subject: nextSubject,
        }));

        onChange({ subject: nextSubject });
    };

    const buildAutoSummary = html => {
        const encodedText = sanitizeNotificationChannelText(
            html,
            CONTENT_CHANNELS.SMS,
        );
        return encodedText.length > 150
            ? `${encodedText.slice(0, 112)}... Please see your Messages.`
            : encodedText;
    };

    const handleEmailContentUpdate = input => {
        const autoSummary = buildAutoSummary(input);
        const autoPushSummary = sanitizeNotificationChannelText(
            input,
            CONTENT_CHANNELS.PUSH,
        );

        const patch = {
            htmlBody: input,
        };

        if (
            shouldAutoSyncChannel({
                isChannelCustom: isPlainTextCustom,
                isAutoLinked: isSmsAutoLinked,
            })
        ) {
            setPlainText(autoSummary);
            setSmsInitialContent(autoSummary);
            setIsSmsAutoLinked(true);
            setSmsEditorVersion(prev => prev + 1);
            patch.plainTextBody = autoSummary;
        }

        if (
            shouldAutoSyncChannel({
                isChannelCustom: isPushTextCustom,
                isAutoLinked: isPushAutoLinked,
            })
        ) {
            setPushText(autoPushSummary);
            setPushInitialContent(autoPushSummary);
            setIsPushAutoLinked(true);
            setPushEditorVersion(prev => prev + 1);
            patch.pushBody = autoPushSummary;
        }

        setFormData(prevDetails => ({
            ...prevDetails,
            htmlBody: input,
            ...(patch.plainTextBody !== undefined
                ? { plainTextBody: patch.plainTextBody }
                : {}),
            ...(patch.pushBody !== undefined
                ? { pushBody: patch.pushBody }
                : {}),
        }));

        onChange(patch);
    };

    const handleSmsContentUpdate = input => {
        setIsPlainTextCustom(true);
        setIsSmsAutoLinked(false);
        setPlainText(input);
        onChange({ plainTextBody: input });
    };

    const handlePushContentUpdate = input => {
        setIsPushTextCustom(true);
        setIsPushAutoLinked(false);
        setPushText(input);
        onChange({ pushBody: input });
    };

    const handlePushDestinationChange = nextDestination => {
        setFormData(prevDetails => {
            const nextPushLink = '';

            onChange({
                pushDestination: nextDestination,
                pushLink: nextPushLink,
            });

            return {
                ...prevDetails,
                pushDestination: nextDestination,
                pushLink: nextPushLink,
            };
        });
    };

    const updatePushLink = value => {
        setFormData(prevDetails => ({
            ...prevDetails,
            pushLink: value,
        }));

        onChange({ pushLink: value });
    };

    const handlePushLinkChange = event => updatePushLink(event.target.value);

    const pushDestination = formData.pushDestination || 'full-message';
    const isPushLinkDisabled =
        pushDestination in PUSH_DESTINATIONS_WITHOUT_LINK;

    useEffect(() => {
        if (pushDestination !== 'portal' || hasLoadedPortalOptions) {
            return;
        }

        setHasLoadedPortalOptions(true);
        queryPortalPages(true)
            .then(payload => {
                const livePortalOptions = payload.viewModels
                    .filter(portalPage => portalPage.status === 1)
                    .map(portalPage => ({
                        value: portalPage.id,
                        label:
                            portalPage.name +
                            (portalPage.isChild === false ? ' (M)' : ''),
                    }));
                setPortalOptions(livePortalOptions);
            })
            .catch(error => {
                console.error('Failed to query portal pages', error);
            });
    }, [pushDestination, hasLoadedPortalOptions]);

    useEffect(() => {
        if (pushDestination !== 'asset' || hasLoadedAssetOptions) {
            return;
        }

        setHasLoadedAssetOptions(true);
        fetchAssets({ assetTypes: [1, 2], page: 1 }, null, false)
            .then(payload => {
                const availableAssetOptions = payload.viewModels.map(asset => ({
                    value: asset.id,
                    label: asset.name,
                }));
                setAssetOptions(availableAssetOptions);
            })
            .catch(error => {
                console.error('Failed to fetch assets', error);
            });
    }, [pushDestination, hasLoadedAssetOptions]);

    useEffect(() => {
        getPushDestinationTypes()
            .then(setPushDestinationTypes)
            .catch(error => {
                console.error('Failed to fetch push destination types', error);
            })
            .finally(() => setIsLoadingPushDestinationTypes(false));
    }, []);

    useEffect(() => {
        const updatedTextAssets = textAssets.filter(asset =>
            text.includes(asset.url),
        );
        setTextAssets(updatedTextAssets);
    }, [text]);

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets();
        }
    }, [isAddFromAssetsChecked]);

    const handleFileChange = fileValue => {
        setFile(sanitizeFileName(fileValue));
    };

    const resolveUploadAgencyId = selectedAgencyId => {
        if (
            selectedAgencyId !== undefined &&
            selectedAgencyId !== null &&
            selectedAgencyId !== ''
        ) {
            return Number(selectedAgencyId);
        }

        const userAgencies = Array.isArray(user?.jwt?.agencies)
            ? user.jwt.agencies
            : [];

        if (userAgencies.length > 0) {
            return Number(userAgencies[0]);
        }

        return 0;
    };

    const uploadAsset = async () => {
        setIsSaving(true);

        if (!validateForm()) {
            setIsSaving(false);
            return;
        }

        let urlResponse;
        let isValidUrl = false;

        if (!asset.url) {
            try {
                const response = await enqueueUpload(file.name, uploadOptions =>
                    uploadFile(file, uploadOptions),
                );
                if (response.error) {
                    Toast.error(response.message);
                } else {
                    urlResponse = encodeURI(response);
                }
                try {
                    new URL(urlResponse);
                    isValidUrl = true;
                    setAsset(prev => ({
                        ...prev,
                        url: urlResponse,
                    }));
                } catch (error) {
                    Toast.error(getText('msg_3e298875de25'));
                }
            } catch (error) {
                Toast.error(getText('msg_3e298875de25'));
            }
        }

        if (isValidUrl || asset.url) {
            const newAsset = { ...asset };
            newAsset.agencyId = resolveUploadAgencyId(
                formData?.agencyId ?? data?.agencyId,
            );
            if (!newAsset.assetTypeId) newAsset.assetTypeId = 1;
            if (urlResponse) newAsset.url = urlResponse;
            newAsset.fileType = file.name.split('.').pop();
            newAsset.fileSize = formatFileSize(file.size);

            const fileCategory = getFileCategory(file.name);
            if (fileCategory === 'video') {
                newAsset.metaData = await getVideoRuntime(file);
            } else if (fileCategory === 'audio') {
                newAsset.metaData = await getAudioLength(file);
            } else if (fileCategory === 'image') {
                newAsset.metaData = await getImageDimensions(file);
            }

            const createResponse = await createAsset(newAsset, null);
            if (createResponse.error) {
                Toast.error(createResponse.message);
            } else if (
                createResponse.status === 200 &&
                createResponse.data.url
            ) {
                Toast.success(getText('msg_9309271e92fe'));
                switch (isUploadFromDeviceChecked) {
                    case 'text':
                        setTextAssets(prevTextAssets => {
                            // Only add the asset if it doesn't already exist

                            const assetExists = prevTextAssets.some(
                                asset =>
                                    asset.assetId === createResponse.data.id,
                            );
                            if (!assetExists) {
                                return [
                                    ...prevTextAssets,
                                    {
                                        assetId: createResponse.data.id,
                                        assetTypeId:
                                            createResponse.data.assetTypeId,
                                        url: createResponse.data.url,
                                    },
                                ];
                            }
                            return prevTextAssets; // Return the previous state if the asset already exists
                        });
                        break;
                    default:
                        break;
                }
                setIsUploadFromDeviceChecked('');
                setAsset({});
                setFile(null);
            } else {
                Toast.error(getText('msg_9f8fd8f97fd3'));
            }
        }

        setIsSaving(false);
    };

    const validateForm = () => {
        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (!file) {
            Toast.error(getText('msg_e11c5988bd25'));
            return false;
        }

        const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) {
            Toast.error(fileTypeAndSizeValidation);
            return false;
        }

        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }

        return true;
    };

    function updateAsset(asset, key, value) {
        return { ...asset, [key]: value };
    }

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    const loadAssets = async () => {
        try {
            let queryParams = {
                assetTypes: [1, 2],
                page: currentPage,
                pageSize: 12,
            };
            queryParams = {
                ...queryParams,
                fileTypes: ['jpg', 'jpeg', 'png', 'svg'],
            };
            if (searchTerm !== '' && searchTerm !== ' ') {
                queryParams = { ...queryParams, search: searchTerm };
            }
            const fetchedAssets = await fetchAssets(queryParams, null, false);
            setAssets(fetchedAssets.viewModels);
            setTotalPages(fetchedAssets.pageTotal);
        } catch (error) {
            console.error('Failed to fetch assets', error);
            Toast.error(getText('msg_cade149a85b2'));
        }
    };

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets();
        }
    }, [currentPage, searchTerm]);

    const handleClick = index => {
        setActiveCard(activeCard === index ? null : index);
    };

    const addAsset = () => {
        if (activeCard === null) {
            return Toast.error(
                getText('msg_c9d0d3fc9172'),
                null,
                getText('msg_40f0bdba0938'),
            );
        }

        const assetAdding = assets[activeCard];
        switch (isAddFromAssetsChecked) {
            case 'image':
                const imageAssets = [
                    imageAsset1,
                    imageAsset2,
                    imageAsset3,
                    imageAsset4,
                ];
                const assetSetters = [
                    setImageAsset1,
                    setImageAsset2,
                    setImageAsset3,
                    setImageAsset4,
                ];

                // Find the first empty slot
                const emptyIndex = imageAssets.findIndex(asset => !asset.url);

                // Update the thumbnail and the internal image array
                if (emptyIndex !== -1) {
                    assetSetters[emptyIndex]({
                        assetId: assetAdding.id,
                        url: assetAdding.url,
                    });
                }
                break;
            case 'video':
                setVideoAsset({
                    assetId: assetAdding.id,
                    url: assetAdding.url,
                });
                setVideoUrl('');
                break;
            case 'text':
                setTextAssets(prevTextAssets => {
                    // Only add the asset if it doesn't already exist

                    const assetExists = prevTextAssets.some(
                        asset => asset.assetId === assetAdding.id,
                    );
                    if (!assetExists) {
                        return [
                            ...prevTextAssets,
                            { assetId: assetAdding.id, url: assetAdding.url },
                        ];
                    }
                    return prevTextAssets; // Return the previous state if the asset already exists
                });
                break;
            default:
                break;
        }
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    };

    const isExistingNotification = Boolean(data?.id);
    const lastEditedAt = data?.updatedAt || data?.createdAt;
    const editedBy = data?.updatedBy || data?.createdBy;

    return (
        <div
            className={
                useWideLayout
                    ? 'col-12 col-xs-12 col-md-12 col-lg-12 pb-5'
                    : 'col-12 col-xs-12 col-md-12 col-lg-10 m-auto pb-5'
            }
        >
            <div className="file-form-label">
                {getText('msg_8d183dbdcea3')}
                <span
                    style={{
                        color: '#EF4E45',
                        marginLeft: '4px',
                        fontWeight: '500',
                    }}
                >
                    *
                </span>
            </div>
            <input
                onChange={handleOnChangeTitle}
                value={formData.subject || ''}
                style={{
                    height: '48px',
                    borderRadius: '12px',
                    border: 'solid 1px rgba(47, 43, 67, 0.2)',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                }}
                className="form-control"
                type="text"
                placeholder={getText('msg_0a359f97edcb')}
                aria-label={getText('msg_df9820b57cc2')}
            />
            <CharacterLimitIndicator
                maxChars={50}
                inputStr={formData.subject || ''}
            />

            <div className={contentStyles.previewRow}>
                <div className={contentStyles.previewRowLeft}>
                    <span className={contentStyles.previewLabel}>
                        {getText('msg_584654ddda43')}
                    </span>
                    <button
                        type="button"
                        className={contentStyles.previewLink}
                        onClick={() => setIsDefinitionsDrawerOpen(true)}
                    >
                        {getText('msg_83db64acf280')}
                    </button>
                </div>

                {isExistingNotification && (
                    <span
                        className={contentStyles.historyAnchor}
                        ref={historyRef}
                    >
                        <button
                            type="button"
                            className={contentStyles.previewLink}
                            onClick={() => setIsHistoryOpen(prev => !prev)}
                        >
                            {getText('msg_9c0d1e2f3a4b')}
                        </button>

                        {isHistoryOpen && (
                            <div className={contentStyles.historyPopover}>
                                <div className={contentStyles.historyTitle}>
                                    {getText('msg_90ccd6497400')}
                                </div>
                                <div className="d-flex" style={{ gap: '24px' }}>
                                    <div>
                                        <div
                                            className={
                                                contentStyles.historyLabel
                                            }
                                        >
                                            {getText('msg_0c79adcf41ec')}
                                        </div>
                                        <div
                                            className={
                                                contentStyles.historyValue
                                            }
                                        >
                                            {lastEditedAt
                                                ? formatDateToMmmDDYYYY(
                                                      lastEditedAt,
                                                  )
                                                : '—'}
                                        </div>
                                    </div>
                                    <div>
                                        <div
                                            className={
                                                contentStyles.historyLabel
                                            }
                                        >
                                            {getText('msg_6180d5db3ed9')}
                                        </div>
                                        <div
                                            className={
                                                contentStyles.historyValue
                                            }
                                        >
                                            {editedBy || '—'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </span>
                )}
            </div>

            <div className={contentStyles.channelTabs}>
                {CHANNEL_TABS.map(tab => {
                    const isActive = activeChannel === tab.value;

                    return (
                        <button
                            key={tab.value}
                            type="button"
                            className={`${contentStyles.channelTabButton} ${isActive ? contentStyles.channelTabActive : ''}`}
                            onClick={() => setActiveChannel(tab.value)}
                        >
                            {tab.label}
                            <i className={`bi ${tab.icon}`} />
                        </button>
                    );
                })}
            </div>
            <div className={contentStyles.channelBlock}>
                <div
                    className={contentStyles.emailEditorWrapper}
                    style={{
                        display:
                            activeChannel === CONTENT_CHANNELS.EMAIL
                                ? 'block'
                                : 'none',
                    }}
                >
                    <RichTextEditor
                        initialContent={emailInitialContent}
                        onContentUpdate={handleEmailContentUpdate}
                        useAbsoluteLinks={true}
                        onAssetDelete={null}
                    />
                </div>

                <div
                    style={{
                        display:
                            activeChannel === CONTENT_CHANNELS.SMS
                                ? 'block'
                                : 'none',
                    }}
                >
                    <div className={contentStyles.emailEditorWrapper}>
                        <RichTextEditor
                            key={`sms-editor-${smsEditorVersion}`}
                            initialContent={smsInitialContent}
                            onContentUpdate={handleSmsContentUpdate}
                            disableFontStyling={true}
                            disableParagraphStyling={true}
                            disableImage={true}
                            disableVideo={true}
                            disableQRCode={true}
                            disableTable={true}
                            onAssetDelete={null}
                            plainTextLinksOnly={true}
                        />
                    </div>
                    <div className="d-flex justify-content-between align-items-baseline">
                        <CharacterLimitIndicator
                            maxChars={15000}
                            inputStr={htmlToPlainText(plainText)}
                        />

                        <div
                            style={{
                                color: '#6F6B81',
                                fontSize: '12px',
                                marginTop: '8px',
                            }}
                        >
                            {getText('msg_321c3a074c64')}{' '}
                            {Math.ceil(htmlToPlainText(plainText).length / 160)}
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        display:
                            activeChannel === CONTENT_CHANNELS.PUSH
                                ? 'block'
                                : 'none',
                    }}
                >
                    <div className={contentStyles.emailEditorWrapper}>
                        <RichTextEditor
                            key={`push-editor-${pushEditorVersion}`}
                            initialContent={pushInitialContent}
                            onContentUpdate={handlePushContentUpdate}
                            disableFontStyling={true}
                            disableParagraphStyling={true}
                            disableImage={true}
                            disableVideo={true}
                            disableQRCode={true}
                            disableTable={true}
                            disableAllLinks={true}
                            disablePhone={true}
                            onAssetDelete={null}
                        />
                    </div>
                    <CharacterLimitIndicator
                        maxChars={178}
                        inputStr={htmlToPlainText(pushText)}
                    />

                    <div className={contentStyles.pushFields}>
                        <div>
                            <div className="file-form-label">
                                {getText('msg_d42713493ca8')}
                                <Tooltip
                                    html={getText('msg_7a2c9e3f0b1d')}
                                    placement="right"
                                >
                                    <i
                                        className={`bi bi-info-circle ${contentStyles.ingToolTipIconStyling}`}
                                    ></i>
                                </Tooltip>
                            </div>
                            <LoadingWrapper
                                isLoading={isLoadingPushDestinationTypes}
                            >
                                <Dropdown
                                    options={pushDestinationOptions}
                                    defaultValue={
                                        pushDestinationOptions.find(
                                            destination =>
                                                destination.value ===
                                                pushDestination,
                                        )?.label || getText('msg_b04ba49f8486')
                                    }
                                    onChange={handlePushDestinationChange}
                                    disabled={isLoadingPushDestinationTypes}
                                    maxOpenHeight="200px"
                                />
                            </LoadingWrapper>
                        </div>
                        <div>
                            <div className="file-form-label">
                                {getText('msg_d0517071aa37')}
                            </div>
                            {pushDestination === 'menu' && (
                                <Dropdown
                                    options={MENU_LINK_OPTIONS}
                                    defaultValue={
                                        MENU_LINK_OPTIONS.find(
                                            option =>
                                                option.value ===
                                                formData.pushLink,
                                        )?.label || getText('msg_8598222918d3')
                                    }
                                    onChange={updatePushLink}
                                    maxOpenHeight="200px"
                                />
                            )}

                            {pushDestination === 'portal' && (
                                <Dropdown
                                    allowSearch={true}
                                    options={portalOptions}
                                    defaultValue={
                                        portalOptions.find(
                                            option =>
                                                option.value ===
                                                formData.pushLink,
                                        )?.label || getText('msg_3a3061182951')
                                    }
                                    onChange={updatePushLink}
                                    maxOpenHeight="200px"
                                />
                            )}

                            {pushDestination === 'sso' && (
                                <SSODropdown
                                    inlineLabel={false}
                                    label=""
                                    client={true}
                                    selectedSSO={formData.pushLink}
                                    onSSOChange={updatePushLink}
                                    placeholder={getText('msg_fb954a0b17de')}
                                    maxOpenHeight="200px"
                                />
                            )}

                            {pushDestination === 'asset' && (
                                <Dropdown
                                    allowSearch={true}
                                    options={assetOptions}
                                    defaultValue={
                                        assetOptions.find(
                                            option =>
                                                option.value ===
                                                formData.pushLink,
                                        )?.label || getText('msg_11de741638cb')
                                    }
                                    onChange={updatePushLink}
                                    maxOpenHeight="200px"
                                />
                            )}

                            {(pushDestination === 'website' ||
                                isPushLinkDisabled) && (
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.pushLink || ''}
                                    onChange={handlePushLinkChange}
                                    disabled={isPushLinkDisabled}
                                    placeholder={
                                        isPushLinkDisabled
                                            ? getText('msg_89db005e4777')
                                            : getText('msg_b83cfb760fb4')
                                    }
                                    style={{
                                        height: '50px',
                                        borderRadius: '12px',
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ marginTop: '60px' }}>
                <span
                    style={{
                        color: '#EF4E45',
                        marginRight: '4px',
                        fontSize: '14px',
                        fontWeight: '500',
                    }}
                >
                    *
                </span>
                <span
                    style={{
                        color: '#000000',
                        fontSize: '14px',
                        fontWeight: '400',
                    }}
                >
                    {getText('msg_04148d61b0b7')}
                </span>
            </div>

            {/* Upload From Device */}
            {isUploadFromDeviceChecked && (
                <div style={overlayBackground}>
                    <div
                        style={overlayContainer}
                        className="card shadow-sm border-light"
                    >
                        <p
                            style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                marginBottom: '30px',
                            }}
                        >
                            {getText('msg_0625a2d6e606')}
                        </p>
                        <div style={{ width: '100%', paddingTop: '6px' }}>
                            <FileDrop file={file} onChange={handleFileChange} />
                        </div>

                        <InputField
                            label={getText('msg_709a23220f2c')}
                            onChange={value =>
                                setAsset(updateAsset(asset, 'name', value))
                            }
                            maxChars={256}
                            required={true}
                            placeholder={getText('msg_b7ffb9789f09')}
                            value={asset.name}
                        />

                        <div style={{ marginBottom: '24px' }}>
                            <div className="file-form-label">
                                {getText('msg_848eed0fbd54')}
                                <Tooltip
                                    html="Use relevant and specific keywords to help make searching for an asset easier."
                                    placement="right"
                                >
                                    <i
                                        className="bi bi-info-circle"
                                        style={{
                                            paddingLeft: '5px',
                                            fontSize: '13px',
                                            color: 'var(--iNGAGED-Primary-Color)',
                                        }}
                                    ></i>
                                </Tooltip>
                            </div>
                            <TagInput
                                onTagsChange={tags =>
                                    setAsset(
                                        updateAsset(asset, 'assetTags', tags),
                                    )
                                }
                                savedTags={asset.tags}
                            />
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <SwitchField
                                label={getText('msg_0c359a10ae17')}
                                onChange={isChecked =>
                                    setAsset(
                                        updateAsset(
                                            asset,
                                            'assetTypeId',
                                            isChecked ? 1 : 2,
                                        ),
                                    )
                                }
                                tooltip={getText('msg_77453f9dd036')}
                                checked={asset.assetTypeId !== 2}
                            />
                        </div>

                        <div
                            id="footer"
                            style={{
                                height: 'fit-content',
                                width: '100%',
                                padding: '28px 28px 0',
                                display: isUploadFromDeviceChecked
                                    ? 'block'
                                    : 'none',
                            }}
                        >
                            <div
                                type="button"
                                style={{
                                    height: '40px',
                                    width: '128px',
                                    textAlign: 'center',
                                    display: 'inline-block',
                                }}
                                className="float-start button-base button-secondary"
                                onClick={() => setIsUploadFromDeviceChecked('')}
                            >
                                {getText('msg_77dfd2135f4d')}
                            </div>
                            <div
                                type="button"
                                style={{
                                    height: '40px',
                                    width: '128px',
                                    textAlign: 'center',
                                    display: 'inline-block',
                                }}
                                className="float-end button-base ing-button-primary-left"
                                onClick={() => uploadAsset()}
                            >
                                {getText('msg_61cc55aa0453')}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add From Assets */}
            {isAddFromAssetsChecked && (
                <div style={overlayBackground}>
                    <div
                        style={overlayContainer}
                        className="card shadow-sm border-light"
                    >
                        <div
                            className={styles.searchWrapper}
                            style={{ marginBottom: '26px' }}
                        >
                            <input
                                type="text"
                                className={styles.searchInput}
                                style={{ width: '100%' }}
                                placeholder={getText('msg_b18cdf8f03e9')}
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                            <span className={styles.searchIcon}>
                                <i className="bi bi-search"></i>
                            </span>
                        </div>

                        <AssetGridView
                            assets={assets}
                            propertiesModel={true}
                            handleClick={handleClick}
                            handleDoubleClick={() => null}
                            activeCard={activeCard}
                            fullAssetName={false}
                        />

                        {assets.length < 1 && (
                            <NotFoundBox
                                backgroundColor="#f0f4ff"
                                title={
                                    searchTerm
                                        ? 'Sorry, no results found'
                                        : 'Sorry, there are no assets to display'
                                }
                                description={
                                    searchTerm
                                        ? 'Try adjusting your search to find what you are looking for'
                                        : `You can upload a New Asset by clicking "Cancel" on the bottom left corner, then clicking on "Upload from Device"`
                                }
                            />
                        )}

                        <PaginationComponent
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />

                        <div
                            id="footer"
                            style={{
                                height: 'fit-content',
                                width: '100%',
                                padding: '28px 28px 0',
                                display: isAddFromAssetsChecked
                                    ? 'block'
                                    : 'none',
                            }}
                        >
                            <div
                                type="button"
                                style={{
                                    height: '40px',
                                    width: '128px',
                                    textAlign: 'center',
                                    display: 'inline-block',
                                }}
                                className="float-start button-base button-secondary"
                                onClick={() => setIsAddFromAssetsChecked('')}
                            >
                                {getText('msg_77dfd2135f4d')}
                            </div>
                            <div
                                type="button"
                                style={{
                                    height: '40px',
                                    width: '128px',
                                    textAlign: 'center',
                                    display: 'inline-block',
                                }}
                                className="float-end button-base ing-button-primary-left"
                                onClick={() => addAsset()}
                            >
                                {getText('msg_61cc55aa0453')}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <SideDrawer
                isOpen={isDefinitionsDrawerOpen}
                setIsOpen={setIsDefinitionsDrawerOpen}
                header={getText('msg_c60bb0cab7d8')}
                sections={MESSAGE_DEFINITIONS_SECTIONS}
            />
        </div>
    );
};

export default ContentTab;
